/*
 * Copyright (c) 2024. Tooling Team
 */

import { IDENTITY_CONFIG, METADATA_OIDC } from "../utils/authConfig";
import { Log, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { PATHS } from "../utils/constants";
import HeaderService from "../services/headerService";
import { store } from "../app/store";
import { userApi } from "../app/services/userApi";
import {
  beginInteractiveSignin,
  recoverSession,
  registerAuthRecoveryHandlers,
} from "./authRecoveryService";

export default class AuthService {
  UserManager;
  headerService;

  constructor() {
    this.headerService = new HeaderService();
    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      metadata: {
        ...METADATA_OIDC,
      },
    });
    // Logger
    Log.logger = console;
    Log.level = Log.DEBUG;
    this.UserManager.events.addUserLoaded(() => {
      if (window.location.href.indexOf("signin-oidc") !== -1) {
        this.navigateToScreen();
      }
    });
    this.UserManager.events.addSilentRenewError((e) => {
      console.log("silent renew error", e.message);
      beginInteractiveSignin();
    });
    this.UserManager.events.addAccessTokenExpired(() => {
      recoverSession();
    });
    registerAuthRecoveryHandlers({
      renewSession: this.signinSilent,
      clearSession: this.cleanSessionStorage,
      signinRedirect: this.signinRedirect,
    });
  }

  //clean session storage on log out
  cleanSessionStorage = () => {
    sessionStorage.removeItem(
      `oidc.user:${process.env.REACT_APP_AUTH_URL}:${process.env.REACT_APP_IDENTITY_CLIENT_ID}`
    );
    sessionStorage.removeItem(`isAdmin`);
    sessionStorage.removeItem(`userRoles`);
  };

  signinRedirectCallback = () => {
    this.UserManager.signinRedirectCallback().then(async () => {
      if (process.env.REACT_APP_DEVELOPMENT_AUTH_DISABLED === "true") {
        sessionStorage.setItem("isAdmin", "true");
      } else {
        await this.addUserProfileToSession();
      }
      window.location.replace("/"); //redirect to main page for roles and other info to be loaded before user can navigate the app
    });
  };

  getUser = async () => {
    const user = await this.UserManager.getUser();
    if (!user) {
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  };

  signinRedirect = () => {
    this.UserManager.signinRedirect({});
  };

  navigateToScreen = () => {
    window.location.replace("/en/dashboard");
  };

  isAuthenticated = () => {
    const oidcStorage = JSON.parse(
      sessionStorage.getItem(
        `oidc.user:${process.env.REACT_APP_AUTH_URL}:${process.env.REACT_APP_IDENTITY_CLIENT_ID}`
      )
    );
    return !!oidcStorage && !!oidcStorage.access_token;
  };

  signinSilent = async () => {
    const user = await this.UserManager.signinSilent();
    console.log("signed in", user);
    return user;
  };
  signinSilentCallback = () => {
    this.UserManager.signinSilentCallback();
  };

  createSigninRequest = () => {
    return this.UserManager.createSigninRequest();
  };

  logout = () => {
    this.cleanSessionStorage();
    this.signoutRedirectCallback();
  };

  signoutRedirectCallback = () => {
    this.UserManager.signoutRedirectCallback().then(() => {
      localStorage.clear();
      this.signinRedirect();
    });
    this.UserManager.clearStaleState();
  };

  addUserProfileToSession = async () => {
    const response = await store.dispatch(
      userApi.endpoints.getUserProfile.initiate()
    );
    if (response.status === 200) {
      const data = await response.json();
      sessionStorage.setItem("isAdmin", `${data.isAdmin === true}`);
    }
    if (response.status === 403) {
      window.location.replace(PATHS.forbidden.url);
    }
  };
}
